
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ModalEditStudentData from '@/components/templates/manage/ModalEditStudentData.vue'
import { GradeCode } from '@/const/GradeCode'
import { SelectItem } from '@/models/SelectItem'
import { ApiNotFoundAppError } from '@/models/AppErrorClasses'
import { ApiResponseTemporaryStudent } from '@/api/responses/manage/temporaryStudents'
import { BranchesApi } from '@/api/methods/manage/BranchesApi'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { AcademyClassesApi } from '@/api/methods/manage/AcademyClassesApi'
import { TemporaryStudentsApi } from '@/api/methods/manage/TemporaryStudentsApi'

/**
 * 仮生徒登録モーダル
 */
@Component({
  components: {
    ModalEditStudentData,
  },
})
export default class ModalEditTemporaryStudent extends Vue {
  /** 仮生徒登録モーダルのフォーマット */
  @Ref()
  modalEditStudentData!: ModalEditStudentData

  /** 生徒情報更新後の処理 */
  @Prop()
  private afterEditStudent!: () => Promise<void>

  /** 学年一覧 */
  private gradeList: SelectItem[] = []

  /** 学年選択の初期値 */
  private defaultGradeCode = ''

  /** 教室一覧 */
  private branchList: SelectItem[] = []

  /** 教室選択の初期値 */
  private defaultBranchCode = ''

  /** コース一覧 */
  private courseList: SelectItem[] = []

  /** コース選択の初期値 */
  private defaultCourseCode = ''

  /** クラス一覧 */
  private classList: SelectItem[] = []

  /** クラス選択の初期値 */
  private defaultClassCode = ''

  /** ユーザのクッキー情報 */
  private userAcademy = this.$appCookies.authAcademy.get()

  /** 対象生徒のユーザID */
  private targetUserId = 0

  /**
   * 確認ボタン押下時の処理
   */
  private async submit(): Promise<void> {
    this.$loading.start()

    // 生徒情報更新
    await TemporaryStudentsApi.updateTemporaryStudent(
      this.targetUserId,
      this.modalEditStudentData.nickname,
      this.modalEditStudentData.identifyTag,
      this.modalEditStudentData.gradeSelect.selectedValue(),
      Number(this.modalEditStudentData.branchSelect.selectedValue()),
      Number(this.modalEditStudentData.courseSelect.selectedValue()),
      Number(this.modalEditStudentData.classSelect.selectedValue())
    )

    // 生徒情報更新後の処理
    //   メソッド内で生徒一覧の再取得を行う
    await this.afterEditStudent()

    this.$loading.complete()

    // モーダルを閉じる
    this.hide()
  }

  /**
   * モーダルを開く
   *
   * @param userId ユーザID
   */
  public async show(userId: number): Promise<void> {
    // ユーザIDを設定
    this.targetUserId = userId

    this.$loading.start()

    // 仮生徒情報の取得
    let targetTemporaryStudent!: ApiResponseTemporaryStudent
    try {
      targetTemporaryStudent = await TemporaryStudentsApi.getTemporaryStudent(userId)
    } catch (error) {
      if (error instanceof ApiNotFoundAppError) {
        alert('指定ユーザが存在しません。')
        this.$loading.complete()
        return
      }

      // 404エラー以外の場合は例外を投げ直し、共通処理に渡す
      throw error
    }

    // 学年選択設定
    this.setGradeList()
    // 学年の初期値を設定
    this.defaultGradeCode = targetTemporaryStudent.gradeCode

    // 教室選択設定
    await this.setBranchList(targetTemporaryStudent.branchName)

    // コース選択設定
    await this.setCourseList(targetTemporaryStudent.courseName)

    // クラス選択設定
    await this.setClassList(this.defaultCourseCode, targetTemporaryStudent.className)

    // モーダルを開く
    this.modalEditStudentData.show(targetTemporaryStudent.nickname, targetTemporaryStudent.identifyTag)

    this.$loading.complete()
  }

  /**
   * 学年一覧の設定
   */
  private setGradeList(): void {
    // 学年プルダウン設定
    this.gradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })
  }

  /**
   * 教室一覧の設定
   *
   * TODO: 生徒単体取得API実装後に修正
   *
   * @param branchName 教室名
   */
  private async setBranchList(branchName: string | undefined): Promise<void> {
    // 教室一覧取得
    const branches = await BranchesApi.getBranches()

    // 教室プルダウン設定
    this.branchList = branches.map((branch) => {
      return {
        // 教室IDをコードとして使用
        value: String(branch.id),
        label: branch.name,
      }
    })

    const branchCode = this.branchList.find((branch) => branch.label === branchName)?.value

    this.defaultBranchCode = branchCode || this.branchList[0].value
  }

  /**
   * コース選択の設定
   *
   * TODO: 生徒単体取得API実装後に修正
   *
   * @param courseName コース名
   */
  private async setCourseList(courseName: string): Promise<void> {
    // コース一覧取得
    const options = { academyId: this.userAcademy.academyId }
    const courses = await AcademyCoursesApi.getAll(options)

    // コース選択設定
    this.courseList = courses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    const courseCode = this.courseList.find((course) => course.label === courseName)?.value

    this.defaultCourseCode = courseCode || this.courseList[0].value
  }

  /**
   * クラス選択の設定
   *
   * TODO: 生徒単体取得API実装後に修正
   *
   * @param courseCode コース指定
   * @param className クラス名
   */
  private async setClassList(courseCode: string, className?: string): Promise<void> {
    if (!courseCode) {
      this.classList = [{ label: '', value: '' }]
      return
    }

    // クラス一覧取得
    const classes = await AcademyClassesApi.getAll(Number(courseCode))

    // クラス選択設定
    this.classList = classes.map((academyClass) => {
      return {
        // クラスIDをコードとして使用
        value: String(academyClass.id),
        label: academyClass.name,
      }
    })

    const classCode = this.classList.find((academyClass) => academyClass.label === className)?.value

    this.defaultClassCode = classCode || this.classList[0].value
  }

  /**
   * コース選択変更時のイベント
   */
  private async changeCourseSelect(): Promise<void> {
    this.$loading.start()

    // クラス選択設定
    await this.setClassList(this.modalEditStudentData.courseSelect.selectedValue())
    // クラスの選択値を更新
    this.modalEditStudentData.classSelect.setCurrentValue(this.classList[0].value)

    this.$loading.complete()
  }

  /**
   * モーダルを閉じる
   */
  private hide(): void {
    this.modalEditStudentData.hide()
  }
}
